import { Minus, Plus, Trash2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';

const CartItem = ({ item }) => {
  const { updateQuantity, removeFromCart, getItemPrice, getPriceTier } = useCart();
  const { t } = useLanguage();
  const { product, quantity } = item;

  const unitPrice = getItemPrice(item) || 0;
  const tier = getPriceTier(item);
  const total = unitPrice * quantity;

  const tierLabels = {
    detail: t('tier_detail'),
    semi_gros: t('tier_semi_wholesale'),
    gros: t('tier_wholesale'),
  };

  return (
    <div className="flex gap-4 p-4 card-surface" data-testid={`cart-item-${product.id}`}>
      <div className="w-24 h-32 flex-shrink-0 overflow-hidden">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=200';
          }}
        />
      </div>

      <div className="flex-1 flex flex-col justify-between min-w-0">
        <div>
          <p className="text-muted text-xs uppercase tracking-widest mb-1">{product.brand}</p>
          <h3 className="text-heading font-light text-lg truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {product.name}
          </h3>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[color:var(--color-primary)] text-sm" data-testid={`unit-price-${product.id}`}>
              {unitPrice.toFixed(2)}€/{t('product_unit')}
            </span>
            <span className={`text-xs uppercase px-1.5 py-0.5 ${
              tier === 'gros' ? 'bg-green-500/10 text-green-500 border border-green-500/30' :
              tier === 'semi_gros' ? 'bg-[color:rgb(var(--primary-rgb)/0.10)] text-[color:var(--color-primary)] border border-[color:rgb(var(--primary-rgb)/0.30)]' :
              'text-muted'
            }`}>
              {tierLabels[tier] || tier}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => updateQuantity(product.id, quantity - 1)}
              className="p-2 card-surface hover:border-[color:rgb(var(--primary-rgb)/0.30)] transition-colors"
              data-testid={`decrease-quantity-${product.id}`}
            >
              <Minus className="w-4 h-4 text-body" />
            </button>
            <span className="w-12 text-center text-heading" data-testid={`quantity-display-${product.id}`}>
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => updateQuantity(product.id, quantity + 1)}
              disabled={quantity >= product.stock}
              className="p-2 card-surface hover:border-[color:rgb(var(--primary-rgb)/0.30)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`increase-quantity-${product.id}`}
            >
              <Plus className="w-4 h-4 text-body" />
            </button>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-heading font-medium" data-testid={`item-total-${product.id}`}>
              {total.toFixed(2)}€
            </span>
            <button
              type="button"
              onClick={() => removeFromCart(product.id)}
              className="p-2 text-muted hover:text-red-500 transition-colors"
              data-testid={`remove-item-${product.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
